import { DecodedParamsRoute } from 'components/routing/DecodedParamsRoute';
import { useListExternalAccountsQuery } from 'hooks/customerApi';
import { useCustomerApiContext } from 'hooks/useCustomerApiContext';
import React, { Suspense, useCallback, useMemo } from 'react';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { Route, Router, Switch } from 'wouter';

import type { BreadcrumbElement } from './BreadcrumbUtils';
import { BreadcrumbComponent, BreadcrumbError } from './BreadcrumbUtils';

const BreadcrumbIntegrationsWithLinkId = ({
  workspaceId,
  linkId,
}: {
  workspaceId: string;
  linkId: string;
}) => {
  // need to query data about the link
  const { context, token } = useCustomerApiContext();

  const variables = useMemo(
    () => ({
      linkId,
    }),
    [linkId]
  );
  const [{ data, error, fetching }, reExecuteQuery] =
    useListExternalAccountsQuery({
      context,
      pause: !token,
      variables,
    });
  const retry = useCallback(
    () => reExecuteQuery({ requestPolicy: 'network-only' }),
    [reExecuteQuery]
  );

  const { isError, result: link } = getResultOrError(data?.link, error);

  const createElements = useCallback(
    (externalAccountId?: string) => {
      const elements: BreadcrumbElement[] = [];
      elements.push({
        name: 'Integrations',
        url: makePath(['w', workspaceId, 'integrations'], {
          absolute: true,
        }),
      });
      if (link) {
        elements.push({
          name: link.name,
          url: makePath(
            ['w', workspaceId, 'integrations', 'links', 'id', linkId],
            {
              absolute: true,
            }
          ),
        });
      }
      if (externalAccountId && link?.externalAccounts.nodes) {
        const externalAccountName =
          link.externalAccounts.nodes.find(
            (acc) => acc.id === externalAccountId
          )?.name ?? 'External Account';
        elements.push({
          name: externalAccountName,
          url: makePath(
            [
              'w',
              workspaceId,
              'integrations',
              'links',
              'id',
              linkId,
              'accounts',
              externalAccountId,
            ],
            {
              absolute: true,
            }
          ),
        });
      }
      return elements;
    },
    [link, linkId, workspaceId]
  );

  if (isError) {
    return (
      <BreadcrumbError
        baseElement={{
          name: 'Integrations',
          url: makePath(['w', workspaceId, 'integrations'], {
            absolute: true,
          }),
        }}
        retry={retry}
        fetching={fetching}
      />
    );
  }

  const base = makePath([
    'w',
    workspaceId,
    'integrations',
    'links',
    'id',
    linkId,
  ]);

  return (
    <Router base={base} key={base}>
      <Switch>
        {/* external account or external account tx page */}
        <DecodedParamsRoute path="/accounts/:externalAccountId/:rest*">
          {({ externalAccountId, rest }) => (
            <BreadcrumbComponent
              elements={createElements(externalAccountId)}
              showLoadingText={fetching}
            />
          )}
        </DecodedParamsRoute>
        {/* external account home page */}
        <Route>
          <BreadcrumbComponent
            elements={createElements()}
            showLoadingText={fetching}
          />
        </Route>
      </Switch>
    </Router>
  );
};

export const BreadcrumbIntegrations = ({
  workspaceId,
}: {
  workspaceId: string;
}) => {
  const base = makePath(['w', workspaceId, 'integrations']);
  const baseElements = [
    {
      name: 'Integrations',
      url: makePath(['w', workspaceId, 'integrations'], {
        absolute: true,
      }),
    },
  ];
  return (
    <Suspense
      fallback={<BreadcrumbComponent elements={baseElements} showLoadingText />}
    >
      <Router base={base} key={base}>
        <Switch>
          {/* Link page or link + account page */}
          <DecodedParamsRoute path="/links/id/:linkId/:rest*">
            {({ linkId }) => (
              <BreadcrumbIntegrationsWithLinkId
                workspaceId={workspaceId}
                linkId={linkId}
              />
            )}
          </DecodedParamsRoute>
          {/* Integrations home page */}
          <Route>
            <BreadcrumbComponent elements={baseElements} />
          </Route>
        </Switch>
      </Router>
    </Suspense>
  );
};
